import { useRef, useState, type ReactNode } from 'react';
import type { Adjustment, Contract } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { formatMoney, parseMoneyToMicros } from '../../../billing/money';
import type { AdjustmentDecisionKind } from '../../../api';
import { AdminProblem } from '../../../components/AdminProblem';
import { LoadingBlock } from '../../../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../../components/fields';
import { DateText, DateTimeText, ScrollTable, StatusBadge } from '../../../components/records';
import { WriteDialog, useDialogTarget } from '../../../components/WriteDialog';
import { useAdmin } from '../../../context';
import { ADJUSTMENT_KIND_LABELS, productName } from '../../../format';
import { useAdminRead } from '../../../useAdminRead';
import { adjustablePeriods, contractName, periodLabel } from './shared';

const KIND_CHOICES = [
  { value: 'credit', label: 'Credit', description: "Lowers the period's bill." },
  {
    value: 'debit',
    label: 'Debit',
    description:
      'Raises it. A debit that takes the period past the spend cap is flagged for whoever approves it.',
  },
] as const;
type AdjustmentKind = (typeof KIND_CHOICES)[number]['value'];

interface RequestForm {
  contractId: string;
  periodStart: string;
  kind: AdjustmentKind;
  amount: string;
  reason: string;
}
type RequestErrors = Partial<Record<keyof RequestForm, string>>;

type Dialog = { kind: 'request' } | { kind: AdjustmentDecisionKind; adjustment: Adjustment };

function AdjustmentsTable({
  adjustments,
  contracts,
  busy,
  canManage,
  staffUserId,
  onDecide,
}: {
  adjustments: readonly Adjustment[];
  contracts: readonly Contract[];
  busy: boolean;
  canManage: boolean;
  staffUserId: string;
  onDecide: (adjustment: Adjustment, decision: AdjustmentDecisionKind) => void;
}) {
  const productOf = (contractId: string) => {
    const contract = contracts.find((c) => c.id === contractId);
    return contract ? productName(contract.productKey) : null;
  };
  return (
    <ScrollTable caption="Adjustments" busy={busy} className="admin-table admin-table--adjustments">
      <thead>
        <tr>
          <th scope="col">Period</th>
          <th scope="col">Contract</th>
          <th scope="col">Kind</th>
          <th scope="col" className="admin-num">
            Amount
          </th>
          <th scope="col">Status</th>
          <th scope="col">Requested</th>
          <th scope="col">Decision</th>
          <th scope="col">Spend cap</th>
          {canManage ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {adjustments.map((adjustment) => {
          // By user id: two staff members may share a display name.
          const own = adjustment.requestedById !== null && adjustment.requestedById === staffUserId;
          return (
            <tr key={adjustment.id}>
              <th scope="row">
                <DateText iso={adjustment.periodStart} />
                <span className="admin-cell-secondary">Period start</span>
              </th>
              <td>
                {productOf(adjustment.contractId) ?? <span className="admin-muted">Unknown contract</span>}
                <span className="admin-cell-secondary">
                  <code className="admin-id">{adjustment.contractId}</code>
                </span>
              </td>
              <td>{ADJUSTMENT_KIND_LABELS[adjustment.kind] ?? adjustment.kind}</td>
              <td className="admin-num">{formatMoney(adjustment.amountMicros, adjustment.currency)}</td>
              <td>
                <StatusBadge status={adjustment.status} />
              </td>
              <td className="admin-cell-wrap">
                {adjustment.requestedBy ?? <span className="admin-muted">Unknown</span>}
                <span className="admin-cell-secondary">
                  <DateTimeText iso={adjustment.requestedAt} />
                </span>
                <span className="admin-cell-secondary">{adjustment.reason}</span>
              </td>
              <td className="admin-cell-wrap">
                {adjustment.decidedAt ? (
                  <>
                    {adjustment.decidedBy ?? <span className="admin-muted">Unknown</span>}
                    <span className="admin-cell-secondary">
                      <DateTimeText iso={adjustment.decidedAt} />
                    </span>
                    {adjustment.decisionReason ? (
                      <span className="admin-cell-secondary">{adjustment.decisionReason}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="admin-muted">Waiting for another staff member</span>
                )}
              </td>
              <td>
                {adjustment.exceedsSpendCap ? (
                  <span className="admin-over-cap">Would exceed the cap</span>
                ) : (
                  <span className="admin-muted">Within the cap</span>
                )}
              </td>
              {canManage ? (
                <td>
                  {adjustment.status !== 'pending' ? (
                    <span className="admin-muted">Decided</span>
                  ) : own ? (
                    <span className="admin-muted">Yours: another staff member decides</span>
                  ) : (
                    <div className="admin-row-actions">
                      <button
                        type="button"
                        className="admin-action admin-action--primary"
                        aria-label={`Approve the ${ADJUSTMENT_KIND_LABELS[adjustment.kind]?.toLowerCase() ?? adjustment.kind} of ${formatMoney(adjustment.amountMicros, adjustment.currency)}`}
                        onClick={() => onDecide(adjustment, 'approve')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="admin-action admin-action--danger"
                        aria-label={`Reject the ${ADJUSTMENT_KIND_LABELS[adjustment.kind]?.toLowerCase() ?? adjustment.kind} of ${formatMoney(adjustment.amountMicros, adjustment.currency)}`}
                        onClick={() => onDecide(adjustment, 'reject')}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}

export interface AdjustmentsSectionProps {
  organizationId: string;
  contracts: readonly Contract[];
  /** The contract the section proposes for a new request. */
  selected: Contract | null;
  canManage: boolean;
  /** The signed-in staff member's user id: their own requests offer no decision (another staff member decides). */
  staffUserId: string;
  onNotice: (message: string) => void;
}

/**
 * Adjustments of the organization: credits and debits on open paid periods, each waiting for the approval of
 * another staff member than the one who asked (four eyes; platform-api refuses the requester anyway). Staff with
 * staff.billing.manage request one and decide the others'.
 */
export function AdjustmentsSection({
  organizationId,
  contracts,
  selected,
  canManage,
  staffUserId,
  onNotice,
}: AdjustmentsSectionProps) {
  const { api } = useAdmin();
  const key = `adjustments\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listAdjustments(organizationId, signal));
  const adjustments = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const dialog = useDialogTarget<Dialog>();
  const now = Date.now();

  const [form, setForm] = useState<RequestForm>({
    contractId: '',
    periodStart: '',
    kind: 'credit',
    amount: '',
    reason: '',
  });
  const [errors, setErrors] = useState<RequestErrors>({});
  const [decisionReason, setDecisionReason] = useState('');
  const [decisionProblem, setDecisionProblem] = useState<string | null>(null);
  // The adjustment platform-api created, for the confirmation (it carries the contract's currency).
  const requested = useRef<Adjustment | null>(null);
  const set = <K extends keyof RequestForm>(field: K, value: RequestForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const adjustable = contracts.filter((c) => adjustablePeriods(c, now).length > 0);
  const formContract = adjustable.find((c) => c.id === form.contractId) ?? null;
  const periods = formContract ? adjustablePeriods(formContract, now) : [];

  const openRequest = () => {
    const first = (selected && adjustable.find((c) => c.id === selected.id)) ?? adjustable[0] ?? null;
    setForm({
      contractId: first?.id ?? '',
      periodStart: first ? (adjustablePeriods(first, now)[0]?.start ?? '') : '',
      kind: 'credit',
      amount: '',
      reason: '',
    });
    setErrors({});
    dialog.show({ kind: 'request' });
  };
  const openDecision = (adjustment: Adjustment, decision: AdjustmentDecisionKind) => {
    setDecisionReason('');
    setDecisionProblem(null);
    dialog.show({ kind: decision, adjustment });
  };

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the adjustments" onRetry={read.retry} />;
  } else if (!adjustments) {
    body = <LoadingBlock label="Loading the adjustments" />;
  } else if (adjustments.length === 0) {
    body = (
      <EmptyPageState
        icon={<NavigationGlyph name="WalletCards" />}
        title="No adjustment"
        description="Corrections are adjustments, never edits: a credit or a debit on an open period, approved by a second staff member."
      />
    );
  } else {
    body = (
      <AdjustmentsTable
        adjustments={adjustments}
        contracts={contracts}
        busy={read.loading}
        canManage={canManage}
        staffUserId={staffUserId}
        onDecide={openDecision}
      />
    );
  }

  const target = dialog.target;
  let dialogView: ReactNode = null;
  if (canManage && target?.kind === 'request') {
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title="Request an adjustment"
        description="A credit or a debit on an open paid period. Another staff member approves or rejects it."
        submitLabel="Request the adjustment"
        onSubmit={async () => {
          const found: RequestErrors = {};
          if (!form.contractId) found.contractId = 'Choose a contract.';
          if (!form.periodStart) found.periodStart = 'Choose a period.';
          const micros = parseMoneyToMicros(form.amount);
          if (micros === null) found.amount = 'Enter the amount as a number, for example 50 or 12.50.';
          else if (micros <= 0) found.amount = 'The amount must be above zero.';
          const reason = reasonError(form.reason);
          if (reason) found.reason = reason;
          setErrors(found);
          if (Object.keys(found).length > 0 || micros === null) return 'invalid';
          requested.current = await api.requestAdjustment(organizationId, {
            contractId: form.contractId,
            periodStart: form.periodStart,
            kind: form.kind,
            amountMicros: micros,
            reason: form.reason,
          });
          return undefined;
        }}
        onSaved={() => {
          const created = requested.current;
          onNotice(
            created
              ? `${ADJUSTMENT_KIND_LABELS[created.kind] ?? created.kind} of ${formatMoney(created.amountMicros, created.currency)} requested; it waits for another staff member.`
              : 'The adjustment is requested; it waits for another staff member.',
          );
          read.retry();
        }}
      >
        {adjustable.length === 0 ? (
          <p className="admin-dialog-warning">
            No contract of this organization has a paid period that started, so there is nothing to adjust
            yet.
          </p>
        ) : null}
        <SelectField
          label="Contract"
          value={form.contractId}
          options={[
            { value: '', label: 'Choose a contract' },
            ...adjustable.map((c) => ({ value: c.id, label: contractName(c) })),
          ]}
          onChange={(value) => {
            set('contractId', value);
            const next = adjustable.find((c) => c.id === value);
            set('periodStart', next ? (adjustablePeriods(next, now)[0]?.start ?? '') : '');
          }}
          error={errors.contractId ?? null}
          required
        />
        <SelectField
          label="Period"
          value={form.periodStart}
          options={[
            { value: '', label: 'Choose a period' },
            ...periods.map((p) => ({ value: p.start, label: periodLabel(p) })),
          ]}
          onChange={(value) => set('periodStart', value)}
          hint="The paid period running now, or the one that just ended while it is not rated yet; a trial takes no adjustment."
          error={errors.periodStart ?? null}
          required
        />
        <RadioGroup
          legend="Kind"
          choices={KIND_CHOICES}
          value={form.kind}
          onChange={(value) => set('kind', value as AdjustmentKind)}
        />
        <TextField
          label="Amount"
          value={form.amount}
          onChange={(value) => set('amount', value)}
          hint="In the contract's currency, for example 50 or 12.50."
          error={errors.amount ?? null}
          inputMode="decimal"
          required
        />
        <ReasonField
          value={form.reason}
          onChange={(value) => set('reason', value)}
          error={errors.reason ?? null}
        />
      </WriteDialog>
    );
  } else if (canManage && target && target.kind !== 'request') {
    const { adjustment } = target;
    const amount = formatMoney(adjustment.amountMicros, adjustment.currency);
    const kind = ADJUSTMENT_KIND_LABELS[adjustment.kind]?.toLowerCase() ?? adjustment.kind;
    const approve = target.kind === 'approve';
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={approve ? `Approve the ${kind} of ${amount}?` : `Reject the ${kind} of ${amount}?`}
        description={
          approve
            ? 'The adjustment then applies to the period. The one who asked for it can never approve it.'
            : 'The adjustment is then closed without applying; the request stays in the history.'
        }
        submitLabel={approve ? 'Approve' : 'Reject'}
        tone={approve ? 'default' : 'danger'}
        onSubmit={async () => {
          const problem = reasonError(decisionReason);
          setDecisionProblem(problem);
          if (problem) return 'invalid';
          await api.decideAdjustment(organizationId, adjustment.id, target.kind, decisionReason);
          return undefined;
        }}
        onSaved={() => {
          onNotice(`The ${kind} of ${amount} is ${approve ? 'approved' : 'rejected'}.`);
          read.retry();
        }}
      >
        <p>
          Requested by {adjustment.requestedBy ?? 'an unknown staff member'}: {adjustment.reason}
        </p>
        {adjustment.exceedsSpendCap ? (
          <p className="admin-dialog-warning">
            With the period&apos;s usage, this debit goes past the contract&apos;s spend cap.
          </p>
        ) : null}
        <ReasonField
          label="Decision reason"
          value={decisionReason}
          onChange={setDecisionReason}
          error={decisionProblem}
        />
      </WriteDialog>
    );
  }

  return (
    <>
      <PageSection
        title="Adjustments"
        description="Credits and debits on a period, requested by one staff member and approved or rejected by another."
        action={
          canManage ? (
            <button type="button" className="admin-action admin-action--primary" onClick={openRequest}>
              Request an adjustment
            </button>
          ) : undefined
        }
      >
        {body}
      </PageSection>
      {dialogView}
    </>
  );
}
